import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import {
  Target, Plus, Pencil, Trash2, Loader2, Calendar, Bug, Shield,
  Play, Clock, CheckCircle, Package, ChevronRight, Search, AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const engagementTypes = [
  { value: 'interactive', label: 'Interactive Testing' },
  { value: 'cicd', label: 'CI/CD Scan' },
  { value: 'api_test', label: 'API Assessment' },
  { value: 'pentest', label: 'Penetration Test' },
  { value: 'red_team', label: 'Red Team Exercise' },
  { value: 'bug_bounty', label: 'Bug Bounty' },
];

const engagementStatuses = [
  { value: 'in_progress', label: 'In Progress', color: 'border-blue-500/30 text-blue-500 bg-blue-500/10' },
  { value: 'completed', label: 'Completed', color: 'border-green-500/30 text-green-500 bg-green-500/10' },
  { value: 'on_hold', label: 'On Hold', color: 'border-yellow-500/30 text-yellow-500 bg-yellow-500/10' },
];

const emptyForm = { name: '', description: '', engagement_type: 'interactive', status: 'in_progress', target_start: '', target_end: '', lead: '', test_strategy: '', tags: [], product_id: '' };

export default function EngagementsPage() {
  const navigate = useNavigate();
  const [engagements, setEngagements] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editEng, setEditEng] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [productFilter, setProductFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedEng, setSelectedEng] = useState(null);
  const [engFindings, setEngFindings] = useState([]);
  const [engTests, setEngTests] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => { fetchEngagements(); fetchProducts(); }, []);

  const fetchEngagements = async (pid) => {
    setLoading(true);
    try {
      const url = pid && pid !== 'all' ? `${API}/engagements?product_id=${pid}` : `${API}/engagements`;
      const r = await axios.get(url, { withCredentials: true });
      setEngagements(r.data);
    } catch {} finally { setLoading(false); }
  };
  const fetchProducts = async () => { try { const r = await axios.get(`${API}/products`, { withCredentials: true }); setProducts(r.data); } catch {} };

  useEffect(() => { fetchEngagements(productFilter); }, [productFilter]);

  const handleSave = async () => {
    if (!form.name.trim() || !form.product_id) return;
    setSaving(true);
    try {
      if (editEng) {
        const r = await axios.put(`${API}/engagements/${editEng.engagement_id}`, form, { withCredentials: true });
        setEngagements(prev => prev.map(e => e.engagement_id === editEng.engagement_id ? r.data : e));
        toast.success('Engagement updated');
      } else {
        const r = await axios.post(`${API}/engagements`, form, { withCredentials: true });
        setEngagements(prev => [r.data, ...prev]);
        toast.success('Engagement created');
      }
      closeDialog();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (engId, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Delete this engagement?')) return;
    try {
      await axios.delete(`${API}/engagements/${engId}`, { withCredentials: true });
      setEngagements(prev => prev.filter(e => e.engagement_id !== engId));
      if (selectedEng?.engagement_id === engId) setSelectedEng(null);
      toast.success('Engagement deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const openEdit = (eng, e) => {
    if (e) e.stopPropagation();
    setForm({
      name: eng.name, description: eng.description, engagement_type: eng.engagement_type,
      status: eng.status, target_start: eng.target_start || '', target_end: eng.target_end || '',
      lead: eng.lead || '', test_strategy: eng.test_strategy || '', tags: eng.tags || [],
      product_id: eng.product_id,
    });
    setEditEng(eng);
    setShowDialog(true);
  };

  const closeDialog = () => { setShowDialog(false); setEditEng(null); setForm({ ...emptyForm }); };

  const openDetail = async (eng) => {
    setSelectedEng(eng);
    setLoadingDetail(true);
    try {
      const [fRes, tRes] = await Promise.all([
        axios.get(`${API}/engagements/${eng.engagement_id}/findings`, { withCredentials: true }),
        axios.get(`${API}/engagements/${eng.engagement_id}/tests`, { withCredentials: true }),
      ]);
      setEngFindings(fRes.data);
      setEngTests(tRes.data);
    } catch {} finally { setLoadingDetail(false); }
  };

  const getStatusColor = (st) => engagementStatuses.find(s => s.value === st)?.color || '';
  const getProductName = (pid) => products.find(p => p.product_id === pid)?.name || 'Unknown';

  const filtered = engagements.filter(e => {
    if (statusFilter !== 'all' && e.status !== statusFilter) return false;
    if (search && !e.name.toLowerCase().includes(search.toLowerCase()) && !e.description?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const sevBadge = (sev) => {
    const m = { critical: 'border-red-500/30 text-red-500 bg-red-500/10', high: 'border-orange-500/30 text-orange-500 bg-orange-500/10', medium: 'border-yellow-500/30 text-yellow-500 bg-yellow-500/10', low: 'border-green-500/30 text-green-500 bg-green-500/10', informational: 'border-blue-500/30 text-blue-500 bg-blue-500/10' };
    return m[sev] || '';
  };

  return (
    <div className="space-y-6" data-testid="engagements-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-heading tracking-tight" data-testid="engagements-title">Engagements</h1>
          <p className="text-sm text-muted-foreground mt-1">Security testing engagements linked to products</p>
        </div>
        <Dialog open={showDialog} onOpenChange={(v) => { if (!v) closeDialog(); else setShowDialog(true); }}>
          <DialogTrigger asChild><Button className="gap-2" data-testid="new-engagement-btn"><Plus className="h-4 w-4" /> New Engagement</Button></DialogTrigger>
          <DialogContent className="max-w-lg" data-testid="engagement-dialog">
            <DialogHeader><DialogTitle>{editEng ? 'Edit Engagement' : 'New Engagement'}</DialogTitle></DialogHeader>
            <ScrollArea className="max-h-[65vh]">
              <div className="space-y-4 pt-2 pr-4">
                <div><Label>Product *</Label>
                  <Select value={form.product_id} onValueChange={v => setForm(p => ({ ...p, product_id: v }))}>
                    <SelectTrigger data-testid="eng-product-select"><SelectValue placeholder="Select product" /></SelectTrigger>
                    <SelectContent>{products.map(p => <SelectItem key={p.product_id} value={p.product_id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Q1 2026 Pentest" data-testid="eng-name-input" /></div>
                <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={2} placeholder="Scope and objectives..." data-testid="eng-desc-input" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Type</Label>
                    <Select value={form.engagement_type} onValueChange={v => setForm(p => ({ ...p, engagement_type: v }))}>
                      <SelectTrigger data-testid="eng-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{engagementTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label>Status</Label>
                    <Select value={form.status} onValueChange={v => setForm(p => ({ ...p, status: v }))}>
                      <SelectTrigger data-testid="eng-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{engagementStatuses.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                    </Select></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Start Date</Label><Input type="date" value={form.target_start?.split('T')[0] || ''} onChange={e => setForm(p => ({ ...p, target_start: e.target.value }))} data-testid="eng-start-input" /></div>
                  <div><Label>End Date</Label><Input type="date" value={form.target_end?.split('T')[0] || ''} onChange={e => setForm(p => ({ ...p, target_end: e.target.value }))} data-testid="eng-end-input" /></div>
                </div>
                <div><Label>Test Strategy</Label><Textarea value={form.test_strategy} onChange={e => setForm(p => ({ ...p, test_strategy: e.target.value }))} rows={2} placeholder="Testing approach..." data-testid="eng-strategy-input" /></div>
                <Button onClick={handleSave} className="w-full" disabled={saving || !form.name.trim() || !form.product_id} data-testid="save-engagement-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}{editEng ? 'Update' : 'Create'} Engagement
                </Button>
              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card data-testid="eng-filters"><CardContent className="p-4"><div className="flex flex-wrap gap-3">
        <div className="flex-1 min-w-[180px]"><div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input placeholder="Search engagements..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="eng-search" /></div></div>
        <Select value={productFilter} onValueChange={setProductFilter}><SelectTrigger className="w-[160px]" data-testid="eng-product-filter"><Package className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" /><SelectValue placeholder="Product" /></SelectTrigger><SelectContent><SelectItem value="all">All Products</SelectItem>{products.map(p => <SelectItem key={p.product_id} value={p.product_id}>{p.name}</SelectItem>)}</SelectContent></Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}><SelectTrigger className="w-[140px]" data-testid="eng-status-filter"><SelectValue placeholder="Status" /></SelectTrigger><SelectContent><SelectItem value="all">All Status</SelectItem>{engagementStatuses.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select>
      </div></CardContent></Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{engagements.length}</p><p className="text-xs text-muted-foreground">Total</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-blue-500">{engagements.filter(e => e.status === 'in_progress').length}</p><p className="text-xs text-muted-foreground">In Progress</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-green-500">{engagements.filter(e => e.status === 'completed').length}</p><p className="text-xs text-muted-foreground">Completed</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-yellow-500">{engagements.filter(e => e.status === 'on_hold').length}</p><p className="text-xs text-muted-foreground">On Hold</p></CardContent></Card>
      </div>

      {/* Engagements List */}
      {loading ? <div className="flex items-center justify-center h-[300px]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
       : filtered.length === 0 ? <Card><CardContent className="flex flex-col items-center justify-center py-16"><Target className="h-10 w-10 text-muted-foreground/30 mb-3" /><p className="text-sm text-muted-foreground">No engagements found. Create one to start testing.</p></CardContent></Card>
       : <div className="space-y-3">
        {filtered.map(eng => (
          <Card key={eng.engagement_id} className="group hover:border-primary/30 transition-colors cursor-pointer" onClick={() => openDetail(eng)} data-testid={`engagement-card-${eng.engagement_id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="p-2 rounded-lg bg-primary/10 mt-0.5 shrink-0"><Target className="h-4 w-4 text-primary" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium text-sm">{eng.name}</h3>
                      <Badge variant="outline" className={`text-[10px] ${getStatusColor(eng.status)}`}>{eng.status?.replace('_', ' ')}</Badge>
                      <Badge variant="outline" className="text-[10px] capitalize">{eng.engagement_type?.replace('_', ' ')}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5">
                      <Package className="h-3 w-3" /> {getProductName(eng.product_id)}
                    </p>
                    {eng.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{eng.description}</p>}
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Bug className="h-3 w-3" />{eng.finding_count || 0} findings</span>
                      <span className="flex items-center gap-1"><Shield className="h-3 w-3" />{eng.test_count || 0} tests</span>
                      {eng.target_start && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{new Date(eng.target_start).toLocaleDateString()}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  <Button variant="outline" size="sm" className="h-7 gap-1 text-xs" onClick={(e) => { e.stopPropagation(); navigate(`/scanner?product_id=${eng.product_id}&engagement_id=${eng.engagement_id}`); }} data-testid={`scan-eng-${eng.engagement_id}`}><Play className="h-3 w-3" /> Scan</Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => openEdit(eng, e)} data-testid={`edit-eng-${eng.engagement_id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={(e) => handleDelete(eng.engagement_id, e)} data-testid={`delete-eng-${eng.engagement_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>}

      {/* Engagement Detail Dialog */}
      <Dialog open={!!selectedEng} onOpenChange={() => setSelectedEng(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col" data-testid="eng-detail-dialog">
          {selectedEng && (<>
            <DialogHeader className="shrink-0">
              <div className="flex items-center gap-3">
                <Badge variant="outline" className={getStatusColor(selectedEng.status)}>{selectedEng.status?.replace('_', ' ')}</Badge>
                <DialogTitle className="text-lg font-heading">{selectedEng.name}</DialogTitle>
              </div>
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5"><Package className="h-3 w-3" /> {getProductName(selectedEng.product_id)} &middot; {selectedEng.engagement_type?.replace('_', ' ')}</p>
            </DialogHeader>
            <div className="flex-1 min-h-0 overflow-y-auto space-y-4 mt-3">
              {selectedEng.description && <div><p className="text-sm font-medium mb-1">Description</p><p className="text-sm text-muted-foreground">{selectedEng.description}</p></div>}
              {selectedEng.test_strategy && <div><p className="text-sm font-medium mb-1">Test Strategy</p><p className="text-sm text-muted-foreground">{selectedEng.test_strategy}</p></div>}

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-lg bg-muted/50 text-center"><p className="text-lg font-bold">{engTests.length}</p><p className="text-xs text-muted-foreground">Scans/Tests</p></div>
                <div className="p-3 rounded-lg bg-muted/50 text-center"><p className="text-lg font-bold">{engFindings.length}</p><p className="text-xs text-muted-foreground">Findings</p></div>
                <div className="p-3 rounded-lg bg-red-500/5 text-center"><p className="text-lg font-bold text-red-500">{engFindings.filter(f => f.severity === 'critical').length}</p><p className="text-xs text-muted-foreground">Critical</p></div>
              </div>

              <Separator />

              {/* Tests */}
              {loadingDetail ? <div className="flex items-center justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : (<>
                <div>
                  <p className="text-sm font-medium mb-2">Scans ({engTests.length})</p>
                  {engTests.length === 0 ? <p className="text-xs text-muted-foreground">No scans yet</p> : (
                    <div className="space-y-1.5">
                      {engTests.map(t => (
                        <div key={t.scan_id} className="flex items-center gap-3 p-2.5 rounded-lg border bg-card text-xs" data-testid={`eng-test-${t.scan_id}`}>
                          <Shield className="h-3.5 w-3.5 text-primary shrink-0" />
                          <span className="font-medium">{t.scan_type?.toUpperCase()} {t.scan_mode !== 'standard' ? `(${t.scan_mode})` : ''}</span>
                          <span className="text-muted-foreground font-mono truncate flex-1">{t.target}</span>
                          <Badge variant="outline" className="text-[10px] border-green-500/30 text-green-500">{t.total_findings} findings</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium">Findings ({engFindings.length})</p>
                    {engFindings.length > 0 && <Button variant="outline" size="sm" className="h-6 text-xs gap-1" onClick={() => { setSelectedEng(null); navigate(`/vulnerabilities?product_id=${selectedEng.product_id}`); }}><Bug className="h-3 w-3" /> View All</Button>}
                  </div>
                  {engFindings.length === 0 ? <p className="text-xs text-muted-foreground">No findings yet</p> : (
                    <ScrollArea className="h-[200px]">
                      <div className="space-y-1.5">
                        {engFindings.slice(0, 50).map(f => (
                          <div key={f.vuln_id} className="flex items-center gap-3 p-2.5 rounded-lg border bg-card text-xs" data-testid={`eng-finding-${f.vuln_id}`}>
                            <Badge variant="outline" className={`text-[10px] shrink-0 ${sevBadge(f.severity)}`}>{f.severity}</Badge>
                            <span className="font-medium truncate flex-1">{f.title}</span>
                            <span className="text-muted-foreground font-mono">{f.cvss_score}</span>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </div>
              </>)}

              <div className="flex gap-2">
                <Button className="gap-1.5 flex-1" onClick={() => { setSelectedEng(null); navigate(`/scanner?product_id=${selectedEng.product_id}&engagement_id=${selectedEng.engagement_id}`); }} data-testid="eng-run-scan-btn"><Play className="h-3.5 w-3.5" /> Run Scan</Button>
                <Button variant="outline" className="gap-1.5 flex-1" onClick={() => { setSelectedEng(null); navigate(`/vulnerabilities?product_id=${selectedEng.product_id}`); }} data-testid="eng-view-findings-btn"><Bug className="h-3.5 w-3.5" /> View Findings</Button>
              </div>
            </div>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
